/**
 * @param {string} text
 * @returns {{ prefix: string, end: number, suffix: string } | null}
 */
function parseCounterValue(text) {
  const match = text.trim().match(/^([^\d]*)(\d+(?:\.\d+)?)(.*)$/);
  if (!match) return null;

  return {
    prefix: match[1],
    end: Number.parseFloat(match[2]),
    suffix: match[3],
  };
}

/**
 * @param {HTMLElement} element
 * @param {string} targetText
 */
function animateCounter(element, targetText) {
  const parsed = parseCounterValue(targetText);
  if (!parsed || element.dataset.nuloCounterDone === 'true') return;

  element.dataset.nuloCounterDone = 'true';

  const duration = 2000;
  const startTime = performance.now();
  const { prefix, end, suffix } = parsed;

  element.textContent = `${prefix}0${suffix}`;

  /**
   * @param {number} now
   */
  const tick = (now) => {
    const progress = Math.min((now - startTime) / duration, 1);
    const eased = 1 - (1 - progress) ** 3;
    const current = Math.round(end * eased);

    element.textContent = `${prefix}${current}${suffix}`;

    if (progress < 1) {
      requestAnimationFrame(tick);
    } else {
      element.textContent = targetText;
    }
  };

  requestAnimationFrame(tick);
}

/**
 * @extends {HTMLElement}
 */
class NutritionMeetsLove extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    if (this.dataset.animateCounters !== 'true') return;

    const counters = this.querySelectorAll('[data-nulo-counter]');
    if (!counters.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          const target = entry.target;
          if (!(target instanceof HTMLElement)) continue;

          const value = target.dataset.nuloCounter;
          if (value) animateCounter(target, value);

          this.#observer?.unobserve(target);
        }
      },
      { threshold: 0.35 }
    );

    for (const counter of counters) {
      if (counter instanceof HTMLElement) {
        this.#observer.observe(counter);
      }
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

if (!customElements.get('nutrition-meets-love')) {
  customElements.define('nutrition-meets-love', NutritionMeetsLove);
}
